import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';

import { config } from '../../../src/config';

import './index.scss';
import Logo, { LogoLight } from '../Sidebar/Logo';
import Google from '../icons/Google';
import { useTheme } from '../../context/ThemeContext';
import { useWhiteLabel } from '../../context/WhiteLabelContext';
import Spinner from '../Global/Spinner';
import Github from '../icons/Github';
import ArrowBack from '../icons/ArrowBack';
import accountSwitchingService from '../../services/accountSwitchingService';
import { fetchWithCSRF } from '../../utils/apiUtils';

const Login = () => {
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [message, setMessage] = useState('');
  const [isLoginSuccess, setIsLoginSuccess] = useState(false);
  const [isGLoginSuccess, setIsGLoginSuccess] = useState(false);
  const [isGithubLoginSuccess, setIsGithubLoginSuccess] = useState(false);
  const [isSubmit, setSubmit] = useState(false);
  const [buttonText, setButtonText] = useState('Continue');

  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isGoogleAuthEnabled,
    isGithubAuthEnabled,
    getGithubClientId,
    canSignup,
    getAppName,
    loading: whiteLabelLoading,
    error: whiteLabelError,
    isUnregisteredDomain,
    domainInfo
  } = useWhiteLabel();

  // Check if this is an "add account" flow
  const searchParams = new URLSearchParams(location.search);
  const isAddAccountMode = searchParams.get('mode') === 'add-account';

  // Check if authentication is available
  const isAuthAvailable = () => {
    // If white label is loading, we don't know yet
    if (whiteLabelLoading) return true;

    // If this is an unregistered domain, auth is not available
    if (isUnregisteredDomain) return false;

    // If there's a white label error, auth might not be available
    if (whiteLabelError) return false;

    // Email/password authentication is always available for white label domains
    // OAuth is optional and controlled by white label settings
    return true;
  };

  // Get appropriate error message for auth unavailable state
  const getAuthUnavailableMessage = () => {
    if (isUnregisteredDomain && domainInfo) {
      return `The domain "${domainInfo.domain}" is not registered for access. Please check the URL or contact your administrator.`;
    }

    if (whiteLabelError) {
      return 'Authentication is currently unavailable. Please try again later or contact support.';
    }

    return 'Authentication is not configured for this domain. Please contact your administrator.';
  };

  const handleChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
    setMessage('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    setSubmit(true);
    setButtonText('Signing in...');

    try {
      const response = await fetchWithCSRF(config.API_URI + '/v1/users/login', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(loginData),

      });

      if (!response.ok) {
        throw new Error('Login failed');
      }

      const data = await response.json();
      localStorage.setItem('accessToken', data.accessToken);

      const userResponse = await fetchWithCSRF(config.API_URI + '/v1/users/me', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${data.accessToken}`,
        },
        credentials: 'include',
      });

      if (!userResponse.ok) {
        throw new Error('Failed to fetch user data');
      }

      const userData = await userResponse.json();
      const username = userData.user.username;

      if (isAddAccountMode) {
        // Add account to switching service
        accountSwitchingService.addAccount(userData.user);
        setMessage('Account added successfully. Redirecting...');
        setIsLoginSuccess(true);

        setTimeout(() => {
          // Redirect back to the main app
          window.location.href = '/';
        }, 1500);
      } else {
        // Regular login flow
        setMessage('Login successful. Redirecting...');
        setIsLoginSuccess(true);

        setTimeout(() => {
          window.location.href = `/u/${username}/boards`;
        }, 2000);
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage('Login failed. Invalid credentials.');
      setSubmit(false);
      setButtonText('Continue');
      setTimeout(() => setMessage(''), 5000);
    }
  };


  // Get the appropriate GitHub Client ID
  const getEffectiveGithubClientId = () => {
    const whiteLabelClientId = getGithubClientId();
    return whiteLabelClientId || config.GITHUB_CLIENT_ID;
  };

  const handleGitHubAuth = () => {
    setIsGithubLoginSuccess(true);

    const clientId = getEffectiveGithubClientId();
    // Use centralized OAuth callback domain for all white label subdomains
    const redirectUri = `${config.DOMAIN}/auth/oauth/callback`;
    const scope = 'user:email';
    const randomState = Math.random().toString(36).substring(2, 15);

    // Store the original domain to redirect back after authentication
    const originalDomain = window.location.origin;
    const stateData = {
      state: randomState,
      originalDomain: originalDomain,
      provider: 'github',
      action: 'login'
    };

    localStorage.setItem('oauth_state_data', JSON.stringify(stateData));

    // Use a simple state parameter instead of JSON to avoid encoding issues
    const simpleState = `${randomState}|${btoa(originalDomain)}|github|login`;

    const githubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}&state=${encodeURIComponent(simpleState)}`;

    window.location.href = githubAuthUrl;
  };

  // Handle Google OAuth
  const handleGoogleAuth = useGoogleLogin({
    onSuccess: async (response) => {

      try {
        // For white label domains, include the original domain
        const originalDomain = window.location.origin;

        // Call the unified backend endpoint
        const res = await fetchWithCSRF(config.API_URI + '/v1/users/auth/google', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            token: response.access_token,
            action: 'login',
            originalDomain: originalDomain
          }),
        });

        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.message || 'Google authentication failed');
        }

        const data = await res.json();

        // Token is automatically set in cookies by the server
        setMessage(data.message); // Displays either "Signup successful" or "Login successful"
        setIsGLoginSuccess(true);

        // Fetch user details if needed
        const userResponse = await fetchWithCSRF(config.API_URI + '/v1/users/me', {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
          credentials: 'include',
        });

        if (!userResponse.ok) {
          throw new Error('Failed to fetch user data');
        }

        const userData = await userResponse.json();
        const username = userData.user.username;

        setTimeout(() => {
          window.location.href = `/u/${username}/boards`;
        }, 2000);
      } catch (error) {
        console.error('Error during Google authentication:', error);
        setMessage(error.message || 'Google authentication failed. Please try again.');
        setTimeout(() => setMessage(''), 5000);
      }
    },
    onError: () => {
      setMessage('Google authentication failed. Please try again.');
      setTimeout(() => setMessage(''), 5000);
    },
  });


  const [systemTheme, setSystemTheme] = useState('light'); // Default to light

  useEffect(() => {
    // Check the system theme and set it
    const updateSystemTheme = () => {
      const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setSystemTheme(isDarkMode ? 'dark' : 'light');
    };

    // Initial check
    updateSystemTheme();

    // Add a listener for changes in the system theme
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', updateSystemTheme);

    // Cleanup the event listener on unmount
    return () => {
      mediaQuery.removeEventListener('change', updateSystemTheme);
    };
  }, []);

  // Determine which logo to display
  const currentTheme = theme === 'system' ? systemTheme : theme;

  // Show loading state while white label is loading
  if (whiteLabelLoading) {
    return (
      <>
        <Helmet>
          <title>Loading - {getAppName()}</title>
        </Helmet>
        <div className="login-container">
          <div className="login-box">
            <h1 className="login-logo">
              {currentTheme === 'dark' && <Logo />}
              {currentTheme === 'light' && <LogoLight />}
            </h1>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spinner size={40} strokeWidth={3} />
              <p style={{ marginTop: '10px', color: '#666' }}>Loading...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Show error message if authentication is not available
  if (!isAuthAvailable()) {
    return (
      <>
        <Helmet>
          <title>Authentication Unavailable - {getAppName()}</title>
        </Helmet>
        <div className="login-container">
          <div className="login-box">
            <h1 className="login-logo">
              {currentTheme === 'dark' && <Logo />}
              {currentTheme === 'light' && <LogoLight />}
            </h1>
            <h2 className="login-heading">Authentication Unavailable</h2>
            <p className="error-message" style={{ textAlign: 'center', margin: '20px 0' }}>
              {getAuthUnavailableMessage()}
            </p>
            <div className="login-links" style={{ textAlign: 'center' }}>
              <button
                onClick={() => window.location.reload()}
                className="login-link"
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#0966ff',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Try Again
              </button>
              <span> · </span>
              <a
                href="https://zoobbe.com"
                className="login-link"
              >
                Go to Zoobbe
              </a>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Login - {getAppName()}</title>
      </Helmet>
      <div className="login-container">
        <div className="login-box">
          <h1 className="login-logo">
            {currentTheme === 'dark' && <Logo />}
            {currentTheme === 'light' && <LogoLight />}
          </h1>
          <h2 className="login-heading">
            {isAddAccountMode ? 'Add another account' : 'Log in to continue'}
          </h2>
          {isAddAccountMode && (
            <div className="add-account-notice">
              <button
                type="button"
                className="back-button"
                onClick={() => navigate(-1)}
              >
                <ArrowBack size={20} />
                Back to current account
              </button>
              <p className="notice-text">
                Sign in with a different account to add it to your account switcher.
              </p>
            </div>
          )}
          <form className="login-form" onSubmit={handleLogin}>
            <input
              type="email"
              name="email"
              placeholder="Enter your email"
              value={loginData.email}
              onChange={handleChange}
              className="login-input"
              required
            />
            <input
              type="password"
              name="password"
              placeholder="Enter your password"
              value={loginData.password}
              onChange={handleChange}
              className="login-input"
              required
            />
            <button type="submit" disabled={isSubmit} className="login-button">
              {buttonText}
            </button>

            {message && !isGLoginSuccess && <p className={isLoginSuccess ? 'success-message' : 'error-message'}>{message}</p>}
          </form>
          {/* Only show OAuth section if at least one provider is enabled */}
          {(isGoogleAuthEnabled() || isGithubAuthEnabled()) && (
            <>
              <p className="login-or">Or continue with:</p>
              <div className="login-social-buttons">
                {isGoogleAuthEnabled() && (
                  <button className="social-button google" disabled={isGLoginSuccess} onClick={handleGoogleAuth}>
                    {
                      isGLoginSuccess ? (
                        <Spinner size={20} strokeWidth={5} />
                      ) : (
                        <><Google /> Google</>
                      )
                    }
                  </button>
                )}
                {isGithubAuthEnabled() && (
                  <button className="social-button github" disabled={isGithubLoginSuccess} onClick={handleGitHubAuth}>
                    {
                      isGithubLoginSuccess ? (
                        <Spinner size={20} strokeWidth={5} />
                      ) : (
                        <><Github /> GitHub</>
                      )
                    }
                  </button>
                )}
              </div>
            </>
          )}
          <div className="login-links">
            <Link to="/recovery" className="login-link">Can't log in?</Link>
            {canSignup() && (
              <>
                <span> · </span>
                <Link to="/signup" className="login-link">Create an account</Link>
              </>
            )}
          </div>
          <footer className="login-footer">
            <p className="login-atlassian">Zoobbe, Inc.</p>
            <p className="login-footer-text">One account for all Zoobbe, Inc. products</p>
            <div className="login-privacy">
              <Link to="/legal/privacy-policy" target='_blank' className="login-privacy-link">Privacy Policy</Link>
              <span> · </span>
              <Link to="/legal/terms-of-service" target='_blank' className="login-privacy-link">Terms</Link>
            </div>
            <p className="login-footer-note">
              This site is protected by reCAPTCHA and the Google <Link to="https://policies.google.com/privacy" target="_blank" className="login-privacy-link">Privacy Policy</Link> and <Link to="https://policies.google.com/terms" target='_blank' className="login-privacy-link">Terms of Service</Link> apply.
            </p>
          </footer>
        </div>
      </div>
    </>
  );
};

export default Login;
